<template>
  <v-card class="confirmation" flat>
    <template v-if="!result">
      <v-card-title>{{ $t("booking.confirm_your_booking") }}</v-card-title>
      <v-card-text>
        <p><strong>{{ $t("booking.service_label") }}</strong> {{ service.name }}</p>
        <p><strong>{{ $t("booking.date_time_label") }}</strong> {{ formattedTime }}</p>
        <p><strong>{{ $t("booking.duration_label") }}</strong> {{ $t("booking.duration_minutes", { n: service.duration }) }}</p>
        <p><strong>{{ $t("booking.price_label") }}</strong> {{ service.price }}</p>
        <p class="confirmation__note">
          {{ $t("booking.payment_note") }}
        </p>

        <v-textarea
          v-model="notes"
          :label="$t('booking.special_requests')"
          rows="2"
          density="comfortable"
        />

        <v-alert v-if="errorMessage" type="error" density="compact" class="mt-3">
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
      <v-card-actions class="confirmation__actions">
        <v-btn variant="text" @click="$emit('change')">{{ $t("booking.change") }}</v-btn>
        <v-spacer />
        <v-btn color="primary" :loading="loading" @click="submit">{{ $t("booking.confirm_booking") }}</v-btn>
      </v-card-actions>
    </template>

    <template v-else>
      <v-card-title>{{ $t("booking.booking_confirmed") }}</v-card-title>
      <v-card-text>
        <p>{{ $t("booking.booking_reference_is") }}</p>
        <p class="confirmation__reference">{{ result.booking_reference }}</p>
        <p>{{ formattedTime }} &mdash; {{ service.name }}</p>
        <p>{{ $t("booking.confirmation_email_on_way") }}</p>
      </v-card-text>
    </template>
  </v-card>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import axios from "axios";
import { useI18n } from "vue-i18n";
import { createGuestAppointment } from "../../services/public-booking-api.service";
import {
  GuestAppointment,
  PublicService,
  WalkInCustomerDTO,
} from "../../types/public-booking.type";

const { t } = useI18n();

const props = defineProps<{
  companyId: number | string;
  service: PublicService;
  startTime: string;
  employeeId: number | null;
  customer?: WalkInCustomerDTO | null;
}>();

const emit = defineEmits<{ change: []; booked: [GuestAppointment] }>();

const notes = ref("");
const loading = ref(false);
const errorMessage = ref("");
const result = ref<GuestAppointment | null>(null);

const formattedTime = computed(() =>
  new Date(props.startTime).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
);

const submit = async () => {
  errorMessage.value = "";
  loading.value = true;
  try {
    const appointment = await createGuestAppointment({
      company_id: Number(props.companyId),
      service_id: props.service.id,
      employee_id: props.employeeId,
      start_time: props.startTime,
      notes: notes.value,
      customer: props.customer ?? undefined,
    });
    result.value = appointment;
    emit("booked", appointment);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      errorMessage.value = t("booking.slot_taken_error");
    } else if (axios.isAxiosError(error)) {
      errorMessage.value = error.response?.data?.errors?.[0]?.title || t("booking.could_not_create_booking");
    } else {
      errorMessage.value = t("booking.could_not_create_booking");
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped>
@use "../../styles/_breakpoints" as bp;

.confirmation {
  &__note {
    font-size: 12px;
    color: #888;
  }

  &__reference {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 1px;
  }

  &__actions {
    @include bp.small-phone {
      flex-direction: column-reverse;
      align-items: stretch;
      gap: 8px;

      .v-spacer {
        display: none;
      }

      .v-btn {
        width: 100%;
        margin: 0;
      }
    }
  }
}
</style>
